import {dataTestId} from '@hconnect/uikit'
import {BoxProps, Collapse, Skeleton} from '@mui/material'
import moment from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {CollapsibleCard} from '../../../shared/components/CollapsibleCard'
import {PlannerLSSettingsKeys} from '../../../shared/enums'
import {useMaterialsById, useStockDevelopmentQuery} from '../../../shared/hooks/api'
import {usePersistingSettingState} from '../../../shared/hooks/usePersistingSettingState'
import {usePlantConfig} from '../../../shared/hooks/usePlantConfigData'
import {getLocalizedDayMonthTimeFormat, MomentRange} from '../../../shared/selectors/time'
import {usePlanningChartScheduleData} from '../../page-planning/dailyPlanning/PlanningChartScheduleDataProvider'
import {usePlanningChartStartEnd} from '../../page-planning/dailyPlanning/PlanningChartStartEndProvider'

import {OptimizerStockLevelsCardContent} from './OptimizerStockLevelsCardContent'
import {OptimizerStockLevelsCardHeader} from './OptimizerStockLevelsCardHeader'
import {SelectedMaterialIdProvider, useSelectedMaterialId} from './SelectedMaterialIdProvider'

export const OptimizerStockLevelsCardSubtitle = () => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {timezone_id: timezoneId} = usePlantConfig()

  const {materialId} = useSelectedMaterialId()

  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const planRange = [startOfPlan, endOfPlan] as MomentRange
  const {scheduleId} = usePlanningChartScheduleData()

  const {data: stockDevelopment, dataUpdatedAt} = useStockDevelopmentQuery({
    materialIds: materialId !== undefined ? [materialId] : [],
    scheduleId,
    timeframe: planRange
  })

  if (materialId === undefined) return null

  if (!stockDevelopment) {
    return <Skeleton variant="rectangular" />
  }

  return (
    <>
      {t('common.lastUpdate', {
        date: getLocalizedDayMonthTimeFormat(moment.utc(dataUpdatedAt), timezoneId, language)
      })}
    </>
  )
}

export const OptimizerStockLevelsCardTitle = () => {
  const {t} = useTranslation()

  const {data: materialsById} = useMaterialsById()

  const {materialId} = useSelectedMaterialId()
  if (materialId === undefined) {
    return <>{t('optimizer.stockLevels')}</>
  }

  if (!materialsById) {
    return <Skeleton variant="rectangular" />
  }
  return <>{materialsById[materialId]?.name}</>
}

export const OptimizerStockLevelsCard = (props: BoxProps) => {
  const [{isCardOpen}, setSettings] = usePersistingSettingState(
    PlannerLSSettingsKeys.OptimizerPageSettings,
    {isCardOpen: true}
  )

  const toggleIsMinimized = useCallback(() => {
    setSettings({isCardOpen: !isCardOpen})
  }, [setSettings, isCardOpen])

  return (
    <SelectedMaterialIdProvider>
      <CollapsibleCard
        {...props}
        isCollapsed={!isCardOpen}
        onToggleCollapsed={toggleIsMinimized}
        title={<OptimizerStockLevelsCardTitle />}
        subtitle={<OptimizerStockLevelsCardSubtitle />}
        headerContent={<OptimizerStockLevelsCardHeader isCollapsed={!isCardOpen} />}
        {...dataTestId('optimizer_stock_levels_card')}
      >
        <Collapse in={isCardOpen}>
          <OptimizerStockLevelsCardContent />
        </Collapse>
      </CollapsibleCard>
    </SelectedMaterialIdProvider>
  )
}
